import { useLayoutEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth';
import { isExhibitionKioskUser } from '@/lib/exhibition';
import { userNeedsSupplierOnboarding } from '@/lib/supplierOnboarding';
import { Loading } from '@/components/Loading';
import { LlmChatDrawerProvider } from '@/components/llm-chat/llm-chat-drawer-context';
import { LlmChatDrawer } from '@/components/llm-chat/LlmChatDrawer';

/**
 * Redirects supplier users who must complete onboarding. Renders the route tree or a full-screen
 * loader so the main app shell never mounts until they are on /onboarding.
 */
export function SupplierOnboardingRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAuth({ tmp: 'SupplierOnboardingRedirect' });
  const path = location.pathname;

  const skipGate = path === '/design-system';
  const need = userNeedsSupplierOnboarding(user);
  const isKiosk = isExhibitionKioskUser(user);

  useLayoutEffect(() => {
    if (skipGate || isLoading) return;

    if (!user) {
      if (path === '/onboarding') {
        navigate('/auth/login', { replace: true });
      }
      return;
    }

    if (need && path !== '/onboarding') {
      navigate('/onboarding', { replace: true });
      return;
    }

    if (!need && path === '/onboarding') {
      navigate('/', { replace: true });
    }
  }, [skipGate, isLoading, user, need, path, navigate]);

  const blockOutlet =
    !skipGate && !isLoading && !!user && need && path !== '/onboarding';

  if (blockOutlet) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <LlmChatDrawerProvider>
      <Outlet />
      {!isKiosk && <LlmChatDrawer />}
    </LlmChatDrawerProvider>
  );
}
